import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Upload } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger
} from '@/components/ui/dialog';

import { createAssetSchema } from "@tyto/shared";
import type { z } from 'zod';

type UploadAssetFormValues = z.infer<typeof createAssetSchema>;

interface AssetUploadDialogProps {
  recordId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerButton?: React.ReactNode;
}

const AssetUploadDialog: React.FC<AssetUploadDialogProps> = ({
  recordId,
  open,
  onOpenChange,
  triggerButton
}) => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [assetContributor, setAssetContributor] = useState<string>('');
  const [assetNote, setAssetNote] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Upload asset mutation
  const uploadAssetMutation = useMutation({
    mutationFn: async (data: UploadAssetFormValues) => {
      if (!selectedFile) {
        throw new Error('No file selected');
      }
      // Step 1: Get the presigned URL from our API
      const createResponse = await fetch(`/api/record/${recordId}/asset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        setError(errorData.error || 'Failed to initialize asset upload');
        throw new Error(errorData.error || 'Failed to initialize asset upload');
      }

      // Parse the response JSON only once and extract all needed data
      const responseData = await createResponse.json();
      const { assetId, presignedUrl } = responseData;

      // Step 2: Upload the file directly to S3 using the presigned URL
      try {
        // For presigned URLs, we must use the exact HTTP method (PUT)
        // and not modify the request with additional parameters
        // that weren't part of the signature calculation

        console.log("🚀 ~ presignedUrl:", new URL(presignedUrl).searchParams);

        const uploadResponse = await fetch(presignedUrl, {
          method: 'PUT',
          body: selectedFile,
          headers: {
            'Content-Type': selectedFile.type,
          }
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`);
        }
      } catch (error) {
        console.error('Error uploading to S3:', error);
        setError(error.message);
        throw error;
      }

      // The upload handling is now contained in the try/catch block above

      // Step 3: Notify our API that the upload is complete
      const completeResponse = await fetch(`/api/record/asset/${assetId}/uploadComplete`, {
        method: 'PUT',
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        throw new Error(errorData.error || 'Failed to complete asset upload');
      }

      return { assetId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordAssets', recordId] });
      onOpenChange(false);
      setSelectedFile(null);
      setAssetContributor('');
      setAssetNote('');
    },
    retry: false
  });

  // File input change handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Asset upload handler
  const handleAssetUpload = async () => {
    if (!selectedFile) return;
    if (!assetContributor.trim()) {
      alert('Please enter your name');
      return;
    }

    // Upload asset with metadata only - file will be uploaded directly to S3
    uploadAssetMutation.mutate({
      type: 'IMAGE',
      mimeType: selectedFile.type,
      assetBy: assetContributor.trim(),
      note: assetNote.trim(),
      fileName: selectedFile.name,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {triggerButton && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload New Asset</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid w-full max-w-sm items-center gap-1.5">
            <label htmlFor="assetContributor" className="text-sm font-medium">
              Contributor Name
            </label>
            <Input
              id="assetContributor"
              value={assetContributor}
              onChange={(e) => setAssetContributor(e.target.value)}
              placeholder="Enter your name"
            />
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5">
            <label htmlFor="asset" className="text-sm font-medium">
              Select Image
            </label>
            <Input
              id="asset"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
            {selectedFile && (
              <p className="text-xs text-muted-foreground">
                {selectedFile.name} ({Math.round(selectedFile.size / 1024)} KB)
              </p>
            )}
          </div>

          <div className="grid w-full max-w-sm items-center gap-1.5">
            <label htmlFor="assetNote" className="text-sm font-medium">
              Note (optional)
            </label>
            <Input
              id="assetNote"
              value={assetNote}
              onChange={(e) => setAssetNote(e.target.value)}
              placeholder="Add a note about this image"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAssetUpload}
            disabled={!selectedFile || uploadAssetMutation.isPending}
          >
            {uploadAssetMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssetUploadDialog;
